import { ExtendedAccountColumns, ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useModalSlideoutScroll } from '@juvare/common-web-ui/ModalSlideout';
import { PartialError } from '@juvare/common-web-ui/PartialError';
import { accountEntityName, getAccountPermissions } from '@juvare/contact-management/backend/features/accounts/shared';
import { AccountDetailsProps } from '@juvare/contact-management/ui/features/accounts';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useStorageGet } from '@juvare/platform-sdk-react/ui/hooks';
import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { RmsAccountDetailsComponent } from './RmsAccountDetailsComponent';

type RmsAccountDetailsComponentContainerProps = {
    accountId: string;
} & Pick<AccountDetailsProps, 'menuItems' | 'onClose'>;

export const RmsAccountDetailsComponentContainer: React.FC<RmsAccountDetailsComponentContainerProps> = ({
    accountId,
    menuItems,
    onClose,
}) => {
    const { isScrolledToTop, handleScroll, handleResetScroll } = useModalSlideoutScroll();
    const userState = useUserState();
    const settings = useSelector(getSettings);
    const { data: account, isLoading, error } = useStorageGet<ExtendedAccountData, ExtendedAccountColumns>(accountEntityName, accountId);

    if (isLoading) return <CircularProgress />;
    if (error) return <PartialError error={error} />;
    if (!account?.data) return null;

    const handleClose = (changed: boolean) => {
        onClose(changed);
        handleResetScroll();
    };

    const accountPermissions = getAccountPermissions(settings, userState, accountId, account.data);

    return (
        <Box
            onScroll={handleScroll}
            sx={{
                overflow: 'auto',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: themeColors.surface.primary,
            }}
        >
            <RmsAccountDetailsComponent
                isScrolledToTop={isScrolledToTop}
                account={account}
                accountPermissions={accountPermissions}
                menuItems={menuItems}
                onClose={handleClose}
            />
        </Box>
    );
};
